import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { differenceInDays, startOfDay } from 'date-fns/esm'
import { isEmpty, isNil } from 'ramda'

import { mixpanel } from '@/mixpanel'
import { getEncounterSpecialisation } from '@/modules/encounter/utils/encounter-mapping'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { HomeSectionLayout } from '../HomeSectionLayout'
import { Card } from './card/Card'

import type { FC } from 'react'
import type { Appointment } from '@/modules/appointment/types/appointment.types'
import type { Encounter } from '@/modules/encounter/types/encounter.types'
import type { CardProps } from './card/Card'

type Props = {
  upcomingAppointment?:
    | ((Appointment | Encounter) & {
        type: 'appointment' | 'encounter'
      })
    | null
}

/**
 * This component is responsible for rendering the upcoming appointment card.
 * It fetches the upcoming appointment or encounter from the API and formats it to the card props.
 */
export const UpcomingAppointmentSection: FC<Props> = ({ upcomingAppointment }) => {
  // Render
  // ------------------------------------------------------------------------- /
  if (isNil(upcomingAppointment)) {
    return null
  }

  const renderUpcomingCard = () => {
    const upcomingCardProps =
      upcomingAppointment?.type === 'appointment'
        ? formatAppointmentToUpcomingCardProps(upcomingAppointment as Appointment)
        : formatEncounterToUpcomingCardProps(upcomingAppointment as Encounter)

    return <Card {...upcomingCardProps} onClick={() => mixpanel.track('home_upcoming_appointment_click')} />
  }

  return (
    <HomeSectionLayout
      title={t({
        id: 'home.upcoming-appointment-card.title',
        comment: 'Upcoming appointment title',
        message: 'Eerstvolgende afspraak',
      })}
    >
      {renderUpcomingCard()}
    </HomeSectionLayout>
  )
}

function formatAppointmentToUpcomingCardProps(appointment: Appointment) {
  const startDate = new Date(appointment.start)
  const day = formatLocale(startDate, 'dd/MM/yyyy')
  const time = formatLocale(startDate, 'HH:mm')
  const location = appointment?.meta?.location?.name || appointment?.meta?.esign?.location?.name?.nl || ''
  const specialisation = appointment.meta.esign.specialisation?.title.nl || ''
  const doctor = appointment?.meta?.esign?.doctor?.name || ''
  const hiddenInApp = appointment?.meta?.hiddenInApp

  const descriptions = Capacitor.isNativePlatform()
    ? [day, hiddenInApp ? null : time, location, doctor, specialisation].filter(Boolean)
    : [day, hiddenInApp ? location : `${time} - ${location}`, doctor, specialisation].filter(Boolean)

  const daysToAppointment = differenceInDays(startOfDay(startDate), startOfDay(new Date()))

  return {
    id: appointment.id,
    type: 'Appointment',
    descriptions,
    daysToAppointment: daysToAppointment,
    waitTime: appointment?.meta?.synops?.waitTime,
    careplan: appointment?.activities?.at(0)?.careplan?.display,
    address: appointment?.meta.location?.address,
  } as CardProps
}

function formatEncounterToUpcomingCardProps(encounter: Encounter) {
  const startDate = new Date(encounter.start)
  const day = formatLocale(startDate, 'dd/MM/yyyy')

  const hasConfirmation =
    !encounter?.meta?.requiresConfirmation &&
    !isNil(encounter?.meta?.confirmedDates) &&
    !isEmpty(encounter?.meta?.confirmedDates)

  const time = formatLocale(startDate, 'HH:mm')
  const location = encounter?.meta?.location?.name || ''
  const specialisation = getEncounterSpecialisation(encounter)
  const hiddenInApp = encounter?.meta?.hiddenInApp

  const descriptions: (string | null)[] = []
  descriptions.push(day)
  if (hasConfirmation) {
    if (Capacitor.isNativePlatform()) {
      descriptions.push(hiddenInApp ? null : time, location)
    } else {
      descriptions.push(hiddenInApp ? location : `${time} - ${location}`)
    }
  }
  descriptions.push(specialisation)

  const daysToAppointment = differenceInDays(startOfDay(startDate), startOfDay(new Date()))

  return {
    id: encounter.id,
    type: 'Encounter',
    descriptions: descriptions.filter(Boolean),
    daysToAppointment: daysToAppointment,
    address: encounter?.meta?.location?.address,
  } as CardProps
}
